if (!customElements.get('banner-modular-slider')) {
  class BannerModularSlider extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slider-slide]')];
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.loop = this.dataset.loop === 'true';
      this.autoplay = this.dataset.autoplay === 'true';
      this.playRequested = this.autoplay;
      this.pauseHover = this.dataset.pauseHover === 'true';
      this.interval = (Number(this.dataset.interval) || 5) * 1000;
      this.elapsed = 0;
      this.startedAt = null;
      this.duration = Number.parseInt(getComputedStyle(this).getPropertyValue('--banner-slider-duration'), 10) || 600;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.desktop = matchMedia('(min-width: 751px)');
      this.events = new AbortController();
      this.hovered = false;
      this.focused = false;
      this.editorSelected = false;

      this.addEventListener('click', (event) => this.onClick(event), { signal: this.events.signal, capture: true });
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.events.signal });
      this.addEventListener('touchstart', (event) => this.onTouchStart(event), { signal: this.events.signal, passive: true });
      this.addEventListener('touchend', (event) => this.onTouchEnd(event), { signal: this.events.signal, passive: true });
      this.addEventListener('focusin', (event) => {
        this.focused = !event.target.closest('[data-slider-toggle-play]');
        this.schedule();
      }, { signal: this.events.signal });
      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          this.focused = this.contains(document.activeElement) && !document.activeElement.closest('[data-slider-toggle-play]');
          this.schedule();
        });
      }, { signal: this.events.signal });
      if (this.pauseHover) {
        this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, { signal: this.events.signal });
        this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, { signal: this.events.signal });
      }
      this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal: this.events.signal });
      this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); }, { signal: this.events.signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal: this.events.signal });
      this.reducedMotion.addEventListener('change', () => this.schedule(), { signal: this.events.signal });
      this.desktop.addEventListener('change', () => this.syncVideos(), { signal: this.events.signal });

      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;
        video.addEventListener('playing', () => slide.classList.add('has-playing-video'), { signal: this.events.signal });
        video.addEventListener('error', () => slide.classList.remove('has-playing-video'), { signal: this.events.signal });
      });
      this.syncUI();
      this.syncVideos();
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.transitionTimer);
      clearTimeout(this.clickTimer);
      cancelAnimationFrame(this.progressFrame);
      this.events?.abort();
      this.slides?.forEach((slide) => slide.querySelector('video')?.pause());
    }

    onClick(event) {
      const button = event.target.closest('[data-slider-prev], [data-slider-next], [data-slider-to], [data-slider-toggle-play]');
      if (this.suppressClick && !button) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
        return;
      }
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-slider-toggle-play')) {
        this.playRequested = !this.playRequested;
        if (this.playRequested) this.hovered = false;
        this.schedule();
        return;
      }
      if (button.hasAttribute('data-slider-prev')) this.goTo(this.index - 1, -1);
      if (button.hasAttribute('data-slider-next')) this.goTo(this.index + 1, 1);
      if (button.hasAttribute('data-slider-to')) this.goTo(Number(button.dataset.sliderTo));
    }

    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || this.slides.length < 2) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1), event.key === 'ArrowRight' ? 1 : -1);
      }
    }

    onTouchStart(event) {
      if (event.touches.length !== 1) return;
      this.touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }

    onTouchEnd(event) {
      if (!this.touchStart || this.slides.length < 2) return;
      const deltaX = event.changedTouches[0].clientX - this.touchStart.x;
      const deltaY = event.changedTouches[0].clientY - this.touchStart.y;
      this.touchStart = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
      this.suppressClick = true;
      clearTimeout(this.clickTimer);
      this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 400);
      this.goTo(this.index + (deltaX < 0 ? 1 : -1), deltaX < 0 ? 1 : -1);
    }

    onBlockSelect(event) {
      const slide = event.target.closest('[data-slider-slide]');
      if (!slide || !this.contains(slide)) return;
      this.editorSelected = true;
      this.goTo(this.slides.indexOf(slide));
      this.schedule();
    }

    goTo(requestedIndex, direction) {
      if (this.slides.length < 2) return;
      const count = this.slides.length;
      const nextIndex = this.loop ? (requestedIndex + count) % count : Math.max(0, Math.min(count - 1, requestedIndex));
      if (nextIndex === this.index) { this.schedule(); return; }

      const previous = this.slides[this.index];
      const next = this.slides[nextIndex];
      const travel = direction || (nextIndex > this.index ? 1 : -1);
      clearTimeout(this.transitionTimer);
      this.slides.forEach((slide) => slide.classList.remove('is-leaving', 'is-preparing'));
      next.style.setProperty('--slide-enter', travel > 0 ? '100%' : '-100%');
      previous.style.setProperty('--slide-exit', travel > 0 ? '-100%' : '100%');
      next.classList.add('is-preparing');
      next.offsetWidth;
      previous.classList.remove('is-active');
      previous.classList.add('is-leaving');
      next.classList.remove('is-preparing');
      next.classList.add('is-active');
      this.index = nextIndex;
      this.syncUI();
      this.syncVideos();
      this.schedule(true);
      this.transitionTimer = setTimeout(() => previous.classList.remove('is-leaving'), this.reducedMotion.matches ? 0 : this.duration);
    }

    syncUI() {
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      const current = this.querySelector('[data-slider-current]');
      if (current) current.textContent = String(this.index + 1);
      this.querySelectorAll('[data-slider-to]').forEach((button) => {
        if (Number(button.dataset.sliderTo) === this.index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      const previous = this.querySelector('[data-slider-prev]');
      const next = this.querySelector('[data-slider-next]');
      if (previous) previous.disabled = !this.loop && this.index === 0;
      if (next) next.disabled = !this.loop && this.index === this.slides.length - 1;
    }

    updateProgress(elapsed) {
      if (this.dataset.navigation !== 'dots') return;
      const progress = Math.min(1, elapsed / this.interval);
      this.querySelectorAll('[data-slider-to]').forEach((button) => {
        const index = Number(button.dataset.sliderTo);
        const value = index < this.index ? 1 : index === this.index ? progress : 0;
        button.style.setProperty('--slide-progress', String(value));
      });
    }

    tickProgress() {
      if (this.startedAt === null) return;
      this.updateProgress(this.elapsed + performance.now() - this.startedAt);
      this.progressFrame = requestAnimationFrame(() => this.tickProgress());
    }

    schedule(reset = false) {
      if (this.startedAt !== null) {
        this.elapsed = Math.min(this.interval, this.elapsed + performance.now() - this.startedAt);
        this.startedAt = null;
      }
      clearTimeout(this.timer);
      cancelAnimationFrame(this.progressFrame);
      if (reset) this.elapsed = 0;
      const canPlay = this.playRequested && this.slides.length > 1 && !this.reducedMotion.matches &&
        !document.hidden && !this.hovered && !this.focused && !this.editorSelected &&
        (this.loop || this.index < this.slides.length - 1);
      const toggle = this.querySelector('[data-slider-toggle-play]');
      if (toggle) {
        toggle.disabled = this.reducedMotion.matches || (!this.loop && this.index === this.slides.length - 1);
        toggle.setAttribute('aria-label', this.playRequested ? 'Pausar reproducción' : 'Reproducir diapositivas');
      }
      this.dataset.sliderPlaying = String(this.playRequested && !this.reducedMotion.matches);
      this.setAttribute('aria-live', canPlay ? 'off' : 'polite');
      this.updateProgress(this.elapsed);
      if (canPlay) {
        this.startedAt = performance.now();
        this.timer = setTimeout(() => this.goTo(this.index + 1, 1), Math.max(1, this.interval - this.elapsed));
        if (this.dataset.navigation === 'dots') this.tickProgress();
      }
    }

    prepareVideo(index) {
      const video = this.slides[index]?.querySelector('video');
      const source = video?.querySelector('source[data-src]');
      if (!source || source.hasAttribute('src')) return;
      source.src = source.dataset.src;
      video.preload = 'metadata';
      video.load();
    }

    syncVideos() {
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (!this.desktop.matches || index !== this.index) {
          video.pause();
          video.autoplay = false;
          slide.classList.remove('has-playing-video');
        }
      });
      if (!this.desktop.matches) return;
      this.prepareVideo(this.index);
      const nextIndex = this.index + 1 < this.slides.length ? this.index + 1 : (this.loop ? 0 : -1);
      if (nextIndex >= 0 && nextIndex !== this.index) this.prepareVideo(nextIndex);
      const activeSlide = this.slides[this.index];
      const activeVideo = activeSlide?.querySelector('video');
      if (activeVideo?.querySelector('source[src]')) {
        activeVideo.preload = 'auto';
        activeVideo.autoplay = true;
        activeVideo.play().catch(() => activeSlide.classList.remove('has-playing-video'));
      }
    }
  }

  customElements.define('banner-modular-slider', BannerModularSlider);
}
